import { useEffect, useState } from "react";
import { useAutoResizeTextarea } from "../../../../../hooks/useAutoResizeTextarea";
import { LinkClientData } from "../../../../../views/TaskDetail/Link/type";
import { DEBOUNCE_TIME } from "../../../../../constants/block/LinkData";
import { useDebounce } from "../../../../../hooks/useDebounce";

interface LinkAddDialogUrlInputProps {
  initData: LinkClientData | null;
  setDebouncedValue: (value: string) => void;
  isUrlModified: boolean;
  setIsUrlModified: (value: boolean) => void;
  setIsLinkNameModified: (value: boolean) => void;
}

const LinkAddDialogUrlInput = ({
  initData,
  isUrlModified,
  setIsUrlModified,
  setDebouncedValue,
  setIsLinkNameModified,
}: LinkAddDialogUrlInputProps) => {
  const [urlInput, setUrlInput] = useState("");
  const [isPaste, setIsPaste] = useState(false);

  const debouncedValue = useDebounce(urlInput, {
    delay: isPaste ? DEBOUNCE_TIME.PASTE : DEBOUNCE_TIME.DEFAULT,
  });

  useEffect(() => {
    // 초기 데이터 설정
    if (initData) {
      setUrlInput(initData.url);
      setDebouncedValue(initData.url);
    }

    return () => {
      setUrlInput("");
      setDebouncedValue("");
    };
  }, [initData]);

  useEffect(() => {
    setDebouncedValue(debouncedValue);
  }, [debouncedValue, setDebouncedValue]);

  const updateUrlInput = (value: string) => {
    setUrlInput(value);
    setIsLinkNameModified(false);
    if (!isUrlModified && value !== initData?.url) {
      setIsUrlModified(true);
    }
  };

  const handleUrlInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateUrlInput(e.target.value);
  };

  const handleUrlPaste = () => {
    setIsPaste(true);
  };

  const {
    textareaRef,
    handleFocus,
    handleBlur,
    handleKeyDown,
    textareaStyles,
    handleResizeHeight,
  } = useAutoResizeTextarea(urlInput);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleUrlInputChange(e);
    // 텍스트 변경 후 높이 조정 (Shift+Enter로 인한 줄바꿈 포함)
    setTimeout(() => {
      handleResizeHeight();
    }, 0);
    setIsPaste(false);
  };

  return (
    <div className="flex w-full items-center rounded-[5px] bg-[#252626] px-[6px] py-[6px] transition-all duration-300 focus-within:bg-5">
      <textarea
        ref={textareaRef}
        className="flex-1 resize-none bg-transparent text-[14px] font-normal text-white placeholder-50 outline-none"
        placeholder="URL"
        value={urlInput}
        onChange={handleChange}
        onPaste={handleUrlPaste}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        autoFocus
        rows={1}
        style={textareaStyles}
      />
    </div>
  );
};

export default LinkAddDialogUrlInput;
